/**
 * WinbackAutomationPanel
 *
 * Collapsible card for Reward Manager that configures the AUTOMATED win-back
 * sends. Independent of the Win-Back report config (its own AppSetting), with
 * a "Sync from report config" button to copy the report's algorithm in.
 *
 * DISABLED by default: the Enabled toggle is off until an admin turns it on
 * (with a confirm), and the daily job only sends for enabled automations.
 */
import React, { useState, useEffect } from 'react';
import {
  Zap,
  ChevronDown,
  ChevronRight,
  Save,
  Play,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import api from '../api';
import toast from 'react-hot-toast';
import WinbackAlgorithmConfig from './WinbackAlgorithmConfig';
import { DEFAULT_WINBACK_CONFIG } from '../lib/winbackAlgorithm';

const DEFAULT_AUTOMATION = {
  enabled: false,
  cooldownDays: 30,
  minDaysLapsed: 15,
  maxDaysLapsed: null,
  gameType: 'all',
  maxRecipientsPerRun: null,
  dailyBudgetCap: null,
  expiresAfterDays: 30,
  notificationMessage: "We miss you at EDH Casino! Here's a comp to welcome you back.",
  config: { ...DEFAULT_WINBACK_CONFIG },
  lastRunAt: null,
  lastRunSummary: null,
};

export default function WinbackAutomationPanel() {
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [automation, setAutomation] = useState(DEFAULT_AUTOMATION);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/admin/settings/winback-automations');
      if (res.data?.success && res.data?.automation) {
        setAutomation({ ...DEFAULT_AUTOMATION, ...res.data.automation });
      }
    } catch (e) {
      console.error('Failed to load win-back automation config', e);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (expanded && !loaded) load();
  }, [expanded, loaded]);

  const set = (key, value) => setAutomation((a) => ({ ...a, [key]: value }));

  const persist = async (patch = {}) => {
    setSaving(true);
    try {
      const payload = { ...automation, ...patch };
      const res = await api.put('/admin/settings/winback-automations', payload);
      if (res.data?.success && res.data?.automation) {
        setAutomation({ ...DEFAULT_AUTOMATION, ...res.data.automation });
      }
      toast.success('Automation saved');
    } catch (e) {
      console.error('Failed to save automation', e);
      toast.error('Failed to save automation');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    const next = !automation.enabled;
    if (next) {
      const ok = window.confirm(
        'Enable automated win-backs? The daily job will start sending comps to qualifying lapsed players (respecting the cooldown). You can pause it any time.'
      );
      if (!ok) return;
    }
    setAutomation((a) => ({ ...a, enabled: next }));
    await persist({ enabled: next });
  };

  const runNow = async (dryRun) => {
    setRunning(true);
    try {
      const res = await api.post('/admin/settings/winback-automations/run', { dryRun });
      const r = res.data?.result || {};
      if (r.skipped) {
        toast(`Skipped: ${r.reason}`);
      } else if (dryRun) {
        toast.success(`Preview: ${r.eligible} eligible of ${r.evaluated} lapsed ($${r.budgetUsed || 0})`);
      } else {
        toast.success(`Sent: ${r.assigned} comp(s), $${r.budgetUsed || 0} budget`);
        load();
      }
    } catch (e) {
      console.error('Run failed', e);
      toast.error(e.response?.data?.error || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const syncFromReport = async () => {
    try {
      const res = await api.get('/admin/settings/winback-algorithm');
      if (res.data?.success && res.data?.config) {
        setAutomation((a) => ({ ...a, config: { ...a.config, ...res.data.config } }));
        toast.success('Synced from report config - remember to Save');
      }
    } catch (e) {
      toast.error('Failed to sync from report config');
    }
  };

  const lastRun = automation.lastRunSummary;

  return (
    <div className="bg-gray-800 rounded-xl border border-amber-500/30 overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-700/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown size={18} className="text-amber-400" /> : <ChevronRight size={18} className="text-amber-400" />}
          <Zap className="text-amber-400" size={20} />
          <h2 className="text-lg font-semibold text-white">Automated Win-Backs</h2>
          <span
            className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              automation.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-600/40 text-gray-400'
            }`}
          >
            {automation.enabled ? 'ENABLED' : 'PAUSED'}
          </span>
        </div>
        <span className="text-xs text-gray-400">
          {automation.lastRunAt ? `Last run ${new Date(automation.lastRunAt).toLocaleString()}` : 'Never run'}
        </span>
      </button>

      {expanded && (
        <div className="p-4 border-t border-gray-700 space-y-5">
          {!loaded ? (
            <div className="py-8 text-center text-gray-400">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading...
            </div>
          ) : (
            <>
              {/* Enable toggle + warning */}
              <div className={`rounded-lg p-3 flex items-center justify-between ${automation.enabled ? 'bg-green-500/10 border border-green-500/30' : 'bg-gray-700/40 border border-gray-600'}`}>
                <div className="flex items-start gap-2">
                  <AlertTriangle size={18} className={automation.enabled ? 'text-green-400 mt-0.5' : 'text-amber-400 mt-0.5'} />
                  <div className="text-sm text-gray-300">
                    {automation.enabled
                      ? 'Automation is live. The daily job sends comps to qualifying lapsed players.'
                      : 'Automation is paused. Nothing is sent until you enable it.'}
                  </div>
                </div>
                <button
                  onClick={toggleEnabled}
                  disabled={saving}
                  className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${
                    automation.enabled
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                      : 'bg-green-600 text-white hover:bg-green-500'
                  }`}
                >
                  {automation.enabled ? 'Pause' : 'Enable'}
                </button>
              </div>

              {/* Run controls */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => runNow(true)}
                  disabled={running}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-gray-200 rounded-lg text-sm hover:bg-gray-600 disabled:opacity-50"
                >
                  {running ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
                  Preview (dry run)
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Run the automation now and send comps to all qualifying players?')) runNow(false);
                  }}
                  disabled={running}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-black font-medium rounded-lg text-sm hover:bg-amber-400 disabled:opacity-50"
                >
                  <Play size={14} /> Run now
                </button>
                {lastRun && (
                  <span className="text-xs text-gray-400">
                    Last: {lastRun.assigned ?? 0} sent / {lastRun.eligible ?? 0} eligible / {lastRun.evaluated ?? 0} evaluated
                  </span>
                )}
              </div>

              {/* Filters */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-2">Eligibility Filters</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Cooldown (days)</label>
                    <input type="number" min="0" value={automation.cooldownDays ?? ''} onChange={(e) => set('cooldownDays', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Min Days Lapsed</label>
                    <input type="number" min="1" value={automation.minDaysLapsed ?? ''} onChange={(e) => set('minDaysLapsed', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Max Days Lapsed</label>
                    <input type="number" min="1" placeholder="No limit" value={automation.maxDaysLapsed ?? ''} onChange={(e) => set('maxDaysLapsed', e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Game Type</label>
                    <select value={automation.gameType || 'all'} onChange={(e) => set('gameType', e.target.value)}
                      className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500">
                      <option value="all">All Games</option>
                      <option value="poker">Poker Only</option>
                      <option value="calgame">Cal Games Only</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Max Recipients / Run</label>
                    <input type="number" min="1" placeholder="No limit" value={automation.maxRecipientsPerRun ?? ''} onChange={(e) => set('maxRecipientsPerRun', e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Daily Budget Cap ($)</label>
                    <input type="number" min="1" placeholder="No limit" value={automation.dailyBudgetCap ?? ''} onChange={(e) => set('dailyBudgetCap', e.target.value ? parseFloat(e.target.value) : null)}
                      className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Comp Expires (days)</label>
                    <input type="number" min="1" value={automation.expiresAfterDays ?? ''} onChange={(e) => set('expiresAfterDays', parseInt(e.target.value) || 30)}
                      className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-xs text-gray-400 mb-1">Notification Message</label>
                  <textarea rows={2} value={automation.notificationMessage || ''} onChange={(e) => set('notificationMessage', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500" />
                </div>
              </div>

              {/* Algorithm config (independent of report) */}
              <div className="pt-2 border-t border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-white">Scoring Algorithm (independent)</h4>
                  <button onClick={syncFromReport} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-gray-200 rounded-lg text-xs hover:bg-gray-600">
                    <RefreshCw size={12} /> Sync from report config
                  </button>
                </div>
                <WinbackAlgorithmConfig config={automation.config} onChange={(cfg) => set('config', cfg)} />
              </div>

              {/* Save */}
              <div className="flex justify-end pt-2 border-t border-gray-700">
                <button onClick={() => persist()} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 disabled:opacity-50">
                  {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                  Save Automation
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
